import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { SectionCard } from "../../../components/ui/SectionCard";
import { Switch } from "../../../components/ui/Switch";
import useIsMobile from "../../../hooks/useIsMobile";
import { PendingBackendNotice } from "../../../components/ui/PendingBackendNotice";
import {
  getAiSettings,
  saveAiSettings,
  isEndpointMissing,
} from "./ai/aiSettingsApi";
import { aiErrorMessage } from "./ai/aiErrorMessage";

// Toggle del canal del portal: gatea si los CLIENTES del courier pueden
// iniciar contacto por el widget (backend: portal_channel_enabled en
// helpdesk/ai-settings + 403 PORTAL_DISABLED en los POST del portal).
// No confundir con el toggle de canal "portal" de la IA, que solo decide si
// la IA responde ahí. Guarda al toggle (sin botón): un solo switch no
// amerita el flujo guardar/descartar de AiSettings.
export default function PortalChannelToggle() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [enabled, setEnabled] = useState(true);
  // mode viaja de vuelta tal cual en el PUT (el DTO lo exige; el resto del
  // upsert es parcial y no toca la config de la IA).
  const modeRef = useRef("off");
  const [loadState, setLoadState] = useState("loading"); // loading|ready|backend-missing|error
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  // Texto del fallo de guardado: los 403 del contrato nuevo (rol sin permiso,
  // ajuste por encima del alcance del rol) se explican solos.
  const [saveError, setSaveError] = useState("");
  const savedTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getAiSettings();
        if (!alive) return;
        const settings = data?.settings || {};
        // Backend desplegado pero SIN el campo: misma degradación que un 404
        // (endpoint viejo) — el switch se muestra apagado de controles.
        if (settings.portal_channel_enabled === undefined) {
          setLoadState("backend-missing");
          return;
        }
        modeRef.current = settings.mode || "off";
        setEnabled(settings.portal_channel_enabled);
        setLoadState("ready");
      } catch (err) {
        if (!alive) return;
        setLoadState(isEndpointMissing(err) ? "backend-missing" : "error");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleToggle = async (next) => {
    setEnabled(next); // optimista; se revierte si el PUT falla
    setSaveState("saving");
    setSaveError("");
    try {
      const data = await saveAiSettings({
        mode: modeRef.current,
        portal_channel_enabled: next,
      });
      const saved = data?.settings?.portal_channel_enabled;
      if (saved !== undefined) setEnabled(saved);
      setSaveState("saved");
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveState("idle"), 2500);
    } catch (err) {
      setEnabled(!next);
      setSaveState(isEndpointMissing(err) ? "idle" : "error");
      // Solo un 403 TIPADO (el backend dice qué permiso falta) reemplaza al
      // genérico: un fallo suelto no aporta más que "intenta de nuevo".
      setSaveError(
        (err?.code ? aiErrorMessage(err, t) : "") ||
          t("No se pudo guardar el cambio. Intenta de nuevo."),
      );
      if (isEndpointMissing(err)) setLoadState("backend-missing");
    }
  };

  const disabled = loadState !== "ready" || saveState === "saving";

  const switchControl = (
    <Switch
      id="portal-channel-enabled"
      checked={loadState === "ready" ? enabled : false}
      onChange={handleToggle}
      disabled={disabled}
      ariaLabel={t("Permitir que los clientes te contacten desde el portal")}
    />
  );

  return (
    <SectionCard
      icon={<Globe className="w-4 h-4" aria-hidden="true" />}
      title={t("Portal de clientes")}
      subtitle={t(
        "El widget de ayuda que ven tus clientes dentro del portal.",
      )}
      /* A6 · En móvil SectionCard apila el slot `action` por encima del cuerpo:
         el switch quedaba suelto en su propia línea, 60px por encima de la
         etiqueta que gobierna, y nada indicaba que se correspondían. Bajo 768px
         el control se renderiza dentro del cuerpo, en la misma fila que su
         etiqueta; en ≥768px sigue en la cabecera de la tarjeta, como antes. */
      action={isMobile ? undefined : switchControl}
      bodyClassName="space-y-2"
    >
      <label
        htmlFor="portal-channel-enabled"
        className="flex items-center justify-between gap-3 text-xs font-medium text-fg cursor-pointer md:block"
      >
        <span className="min-w-0">
          {t("Permitir que los clientes te contacten desde el portal")}
        </span>
        {isMobile && <span className="shrink-0">{switchControl}</span>}
      </label>
      <p className="text-[12px] md:text-[11px] text-fg-muted">
        {t(
          "Apagado, el widget no acepta conversaciones nuevas ni mensajes; las conversaciones ya abiertas se siguen pudiendo leer.",
        )}
      </p>
      {loadState === "backend-missing" && (
        <PendingBackendNotice>
          {t("El control del canal del portal está en construcción.")}
        </PendingBackendNotice>
      )}
      {loadState === "error" && (
        <p className="text-[12px] md:text-[11px] text-red-600" role="alert">
          {t("No se pudo cargar la configuración del portal. Recarga la página.")}
        </p>
      )}
      {/* Feedback inline, nunca toast (mismo criterio que AiSettings). */}
      {saveState === "saved" && (
        <p className="text-[12px] md:text-[11px] text-fg-muted" aria-live="polite">
          {t("Cambios guardados.")}
        </p>
      )}
      {saveState === "error" && (
        <p className="text-[12px] md:text-[11px] text-red-600" role="alert">
          {saveError}
        </p>
      )}
    </SectionCard>
  );
}
